/**
 * ExplicitConsentReason Integration Layer
 * Rule: SDK -> Store bridge with UI feedback
 */
import i18next from 'i18next';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import { toast } from 'sonner';
import { useExplicitConsentReasonStore } from '@/store/explicit-consent-reason.store.js';
import { useTenantStore } from '@/store/tenant.store';

export class ExplicitConsentReasonIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch explicit consent reasons and update store
     */
    async fetchExplicitConsentReasons(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'asc', columnFilters = {}) {
        const store = useExplicitConsentReasonStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) {
                        acc[`filter_${key}`] = value;
                    }
                    return acc;
                }, {})
            });

            const { data, pagination, uniqueValues } = response;
            store.setReasons(data || []);

            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page,
                    totalPages: pagination.totalPages,
                    totalItems: pagination.total,
                    itemsPerPage: pagination.pageSize,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            } else {
                store.setPaginationData({
                    currentPage: page,
                    totalPages: Math.ceil((data?.length || 0) / pageSize) || 1,
                    totalItems: data?.length || 0,
                    itemsPerPage: pageSize,
                    hasNextPage: false,
                    hasPreviousPage: false,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Create explicit consent reason
     */
    async createExplicitConsentReason(data) {
        const store = useExplicitConsentReasonStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.create({ ...data, tenantId });
            toast.success(i18next.t('obtainingExplicitConsentReason:messages.createSuccess', { defaultValue: 'Explicit consent reason created' }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update explicit consent reason
     */
    async updateExplicitConsentReason(id, data) {
        const store = useExplicitConsentReasonStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.update(id, { ...data, tenantId });
            toast.success(i18next.t('obtainingExplicitConsentReason:messages.updateSuccess', { defaultValue: 'Explicit consent reason updated' }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete explicit consent reason
     */
    async deleteExplicitConsentReason(id) {
        const store = useExplicitConsentReasonStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.delete(id);
            toast.success(i18next.t('obtainingExplicitConsentReason:messages.deleteSuccess', { defaultValue: 'Explicit consent reason deleted' }));
            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk delete explicit consent reasons
     */
    async bulkDeleteExplicitConsentReasons(ids) {
        const store = useExplicitConsentReasonStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.createBatchDelete({ ids });
            toast.success(i18next.t('obtainingExplicitConsentReason:messages.bulkDeleteSuccess', {
                defaultValue: 'Selected explicit consent reasons deleted',
                count: ids.length,
            }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error, {
            notFoundDefault: 'Explicit consent reason not found',
        });
    }
}
